<template>
  <div class="markdown-body" v-html="html"></div>
</template>

<script setup>
import { computed } from 'vue'
import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js'
// 使用 GitHub 风格的 Highlight.js 主题
import 'highlight.js/styles/github.css'

const props = defineProps({
  source: {
    type: String,
    required: true
  }
})

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  highlight(code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return `<pre class="hljs"><code>${hljs.highlight(code, {
          language: lang,
          ignoreIllegals: true
        }).value}</code></pre>`
      } catch (__) {
        // 如果高亮失败，回退到普通代码块
      }
    }
    return `<pre class="hljs"><code>${md.utils.escapeHtml(code)}</code></pre>`
  }
})

const html = computed(() => md.render(props.source))
</script>

<style>
/* GitHub Markdown 核心样式 (无 scoped 限制，确保完全覆盖) */
.markdown-body {
  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  margin: 0;
  color: #24292f;
  background-color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji';
  font-size: 16px;
  line-height: 1.5;
  word-wrap: break-word;
}

.markdown-body * {
  box-sizing: border-box;
}

.markdown-body > *:first-child {
  margin-top: 0 !important;
}

.markdown-body > *:last-child {
  margin-bottom: 0 !important;
}

/* 标题 */
.markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4, .markdown-body h5, .markdown-body h6 {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-body h1 {
  font-size: 2em;
  margin: 0.67em 0;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #d0d7de;
}

.markdown-body h2 {
  font-size: 1.5em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #d0d7de;
}

.markdown-body h3 {
  font-size: 1.25em;
}

.markdown-body h4 {
  font-size: 1em;
}

.markdown-body h5 {
  font-size: 0.875em;
}

.markdown-body h6 {
  font-size: 0.85em;
  color: #57606a;
}

/* 段落 */
.markdown-body p {
  margin-top: 0;
  margin-bottom: 16px;
}

/* 列表 */
.markdown-body ul, .markdown-body ol {
  margin-top: 0;
  margin-bottom: 16px;
  padding-left: 2em;
}

.markdown-body ul ul, .markdown-body ul ol, .markdown-body ol ol, .markdown-body ol ul {
  margin-top: 0;
  margin-bottom: 0;
}

.markdown-body li {
  word-wrap: break-all;
}

.markdown-body li + li {
  margin-top: 0.25em;
}

/* 链接 */
.markdown-body a {
  color: #0969da;
  text-decoration: none;
}

.markdown-body a:hover {
  text-decoration: underline;
}

/* 引用 */
.markdown-body blockquote {
  margin: 0;
  padding: 0 1em;
  color: #57606a;
  border-left: 0.25em solid #d0d7de;
}

/* 代码 */
.markdown-body code, .markdown-body tt {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(175, 184, 193, 0.2);
  border-radius: 6px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
}

.markdown-body pre {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 85%;
  line-height: 1.45;
  background-color: #f6f8fa;
  border-radius: 6px;
  overflow: auto;
  padding: 16px;
}

.markdown-body pre code, .markdown-body pre tt {
  padding: 0;
  margin: 0;
  font-size: 100%;
  word-break: normal;
  white-space: pre;
  background: transparent;
  border: 0;
  line-height: inherit;
  overflow: visible;
}

/* 表格 - GitHub 经典样式 */
.markdown-body table {
  display: block;
  width: 100%;
  width: max-content;
  max-width: 100%;
  overflow: auto;
  margin-top: 0;
  margin-bottom: 16px;
  border-spacing: 0;
  border-collapse: collapse;
  font-size: 14px;
}

.markdown-body table th {
  font-weight: 600;
  padding: 6px 13px;
  border: 1px solid #d0d7de;
  background-color: #f6f8fa;
  white-space: nowrap;
}

.markdown-body table td {
  padding: 6px 13px;
  border: 1px solid #d0d7de;
  white-space: nowrap;
}

.markdown-body table tr {
  background-color: #ffffff;
  border-top: 1px solid #d0d7de;
}

.markdown-body table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

/* 水平线 */
.markdown-body hr {
  height: 0.25em;
  padding: 0;
  margin: 24px 0;
  background-color: #d0d7de;
  border: 0;
}

/* 图片 */
.markdown-body img {
  max-width: 100%;
  box-sizing: content-box;
  background-color: #ffffff;
  border-style: none;
}

/* 任务列表 */
.markdown-body input[type=checkbox] {
  margin: 0 0.2em 0.25em -1.6em;
  vertical-align: middle;
}

/* 键盘标签 */
.markdown-body kbd {
  display: inline-block;
  padding: 3px 5px;
  font: 11px 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  line-height: 10px;
  color: #24292f;
  vertical-align: middle;
  background-color: #f6f8fa;
  border: solid 1px #d0d7de;
  border-bottom-color: #98a1ab;
  border-radius: 6px;
  box-shadow: inset 0 -1px 0 #98a1ab;
}

/* 高亮代码块适配 GitHub 主题 */
.markdown-body pre.hljs {
  background-color: #f6f8fa !important;
  padding: 16px !important;
  border-radius: 6px;
  border: 1px solid #d0d7de;
}

.markdown-body pre.hljs code {
  background: transparent;
  padding: 0;
}


</style>